// ─────────────────────────────────────────────────────────────────────────────
// WP-15 — Piezas transversales del Admin React (REQ-126): barra "Guardar" (arriba/abajo),
// paginación reutilizable y encabezado de sección. Reescritura nativa del iframe legacy.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES } from '@/i18n/languages';
import type { LocalizedText, CampaignDocumentDto, UploadCampaignDocumentInput } from '@/api/recognition';
import { getRecognitionErrorMessage } from '@/api/recognition';
import { translationApi, SessionExpiredError } from '@/api/client';
import { StateBlock } from '../components';
import { COMODIN_IDIOMA } from '../CampaignDocs';
import { asset } from '@/utils/basePath';

interface SaveBarProps {
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
  /** Deshabilita el guardado con un motivo (ej: campaña iniciada). */
  disabledReason?: string | null;
  saved?: boolean;
  /** Rótulo alterno del botón. Bug UAT #16: dentro de un wizard, «Guardar» a secas se confunde con
   *  «Siguiente»; ahí se pasa «Guardar borrador» para decir qué guarda exactamente. */
  label?: string;
}

/** Barra de acción Guardar (se coloca arriba y abajo de cada sección editable). */
export function AdminSaveBar({ onSave, saving, dirty, disabledReason, saved, label }: Readonly<SaveBarProps>) {
  const { t } = useTranslation('recognition');
  const disabled = saving || !dirty || Boolean(disabledReason);
  return (
    <div className="adm-savebar">
      {disabledReason && <span className="adm-savebar-reason"><i className="fas fa-lock" /> {disabledReason}</span>}
      {saved && !dirty && <span className="adm-savebar-ok"><i className="fas fa-check" /> {t('admin.saved')}</span>}
      <button type="button" className="btn btn--primary" onClick={onSave} disabled={disabled}>
        <i className="fas fa-floppy-disk" /> {saving ? t('admin.saving') : (label ?? t('admin.save'))}
      </button>
    </div>
  );
}

interface AdminToggleProps {
  isOn: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

/** Switch accesible reutilizable (`role="switch"`), extraído del patrón `.adm-toggle` duplicado en
 *  AdminModeration/AdminNotifications/AdminRecognitions/AdminRoleEditorModal. Gestión es el primer
 *  consumidor de esta versión centralizada; los demás archivos no se tocan (fuera de alcance). */
export function AdminToggle({ isOn, onChange, disabled }: Readonly<AdminToggleProps>) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      disabled={disabled}
      className={`adm-toggle ${isOn ? 'adm-toggle--on' : ''}`}
      onClick={() => onChange(!isOn)}
    >
      <span className="adm-toggle-knob" />
    </button>
  );
}

interface OverrideResetLinkProps {
  /** Solo se pinta cuando el override por-programa tiene un valor explícito (no `null`/`undefined`). */
  hasOverride: boolean;
  onReset: () => void;
}

/**
 * WP-E10 — «restablecer a heredado» para un toggle de doc de PROGRAMA (`antiSpoiler`,
 * `allowPostEdit`): una vez que `AdminToggle` escribe `true`/`false`, no hay forma de volver a
 * `null` (hereda el global) porque el switch solo conoce esos dos estados. Sin esta salida, un
 * clic accidental deja el override pegado para siempre — el backend SÍ soporta `null` (un campo
 * ausente en el `ReplaceOneAsync` de `GamificationRepository.SaveProgramSettingsAsync` vuelve a
 * heredar), pero nada en esta pantalla podía llegar a mandarlo. Reproducido en vivo el
 * 2026-08-28: el anti-spoiler de Brillamos quedó en `false` sin que hubiera manera de
 * despegarlo desde la UI, y hubo que corregir el documento directo en Mongo.
 */
export function OverrideResetLink({ hasOverride, onReset }: Readonly<OverrideResetLinkProps>) {
  const { t } = useTranslation('recognition');
  if (!hasOverride) return null;
  return (
    <button type="button" className="adm-override-reset" onClick={onReset}>
      <i className="fas fa-rotate-left" /> {t('admin.campaigns.resetToGlobal')}
    </button>
  );
}

/**
 * WP-15 Task 9 — ícono de ayuda reutilizable junto a un `AdminToggle`, mismo patrón que
 * `AdminVistas.tsx` (`FloatingTooltip` + botón `fa-circle-info`). `tooltipKey` indexa
 * `admin.campaigns.tooltip.<tooltipKey>` (namespace `recognition`). Vive aquí (no en
 * `AdminCampaigns.tsx`) porque lo consumen tanto ese archivo como `CampaignExceptions.tsx`,
 * y ambos ya importan piezas compartidas desde este módulo.
 */
export function ToggleHelp({ tooltipKey }: Readonly<{ tooltipKey: string }>) {
  const { t } = useTranslation('recognition');
  return (
    <FloatingTooltip tooltipClassName="adm-vista-tooltip" content={t(`admin.campaigns.tooltip.${tooltipKey}`)}>
      {(anchorProps) => (
        <button type="button" className="adm-vista-info" aria-label={t('admin.campaigns.tooltipAria', { defaultValue: 'Ayuda' })} {...anchorProps}>
          <i className="fas fa-circle-info" aria-hidden="true" />
        </button>
      )}
    </FloatingTooltip>
  );
}

/** Hook de paginación en memoria reutilizable en todas las tablas del admin. */
export function usePagination<T>(items: T[], initialSize = 25) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  // pageSize === 0 = opción "Todas" del AdminPagerFooter (PAGE_SIZES incluye 0). Sin este guardo,
  // Math.ceil(n/0)=Infinity y items.slice(0,0)=[] dejaban la tabla VACÍA mientras el pie decía
  // "mostrando 1–N de N". Se mapea 0 → todos los items (misma lógica que AdminPagerFooter).
  const size = pageSize === 0 ? Math.max(items.length, 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(items.length / size));
  const safePage = Math.min(page, totalPages);
  const slice = useMemo(
    () => items.slice((safePage - 1) * size, safePage * size),
    [items, safePage, size],
  );
  return { page: safePage, setPage, pageSize, setPageSize, totalPages, slice };
}

interface PaginationProps {
  page: number;
  totalPages: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}

/** Controles de paginación transversales (tamaño de página + navegación). */
export function AdminPagination({ page, totalPages, pageSize, onPage, onPageSize }: Readonly<PaginationProps>) {
  const { t } = useTranslation('recognition');
  return (
    <div className="adm-pagination">
      <label className="adm-pagesize">
        {t('admin.rowsPerPage')}
        <select className="field" value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
          {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <div className="adm-page-nav">
        <button className="btn btn--secondary" type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}><i className="fas fa-chevron-left" /></button>
        <span>{t('wall.pageOf', { page, total: totalPages })}</span>
        <button className="btn btn--secondary" type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)}><i className="fas fa-chevron-right" /></button>
      </div>
    </div>
  );
}

interface PagerFooterProps {
  total: number;
  page: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}

/** Tamaños de página del maquetado (`_pgSizeOptions`). 0 = "Todas". */
export const PAGE_SIZES = [10, 25, 50, 100, 0] as const;

/**
 * Pie de tabla del maquetado (`_pgFooterDynamic`): "Mostrando 1–25 de 120" a la izquierda,
 * navegación numerada (ventana de 5) + selector "N por pág." a la derecha. Es distinto de
 * `AdminPagination` —que es el pie anterior, ya aprobado en otras secciones— y por eso se agrega
 * como componente nuevo en vez de cambiar aquél.
 */
export function AdminPagerFooter({ total, page, pageSize, onPage, onPageSize }: Readonly<PagerFooterProps>) {
  const { t } = useTranslation('recognition');
  const size = pageSize === 0 ? Math.max(total, 1) : pageSize;
  const pages = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(page, pages);
  const start = (safePage - 1) * size;
  const end = Math.min(start + size, total);

  const maxShow = 5;
  let startP = Math.max(1, safePage - Math.floor(maxShow / 2));
  const endP = Math.min(pages, startP + maxShow - 1);
  if (endP - startP < maxShow - 1) startP = Math.max(1, endP - maxShow + 1);
  const numbers = Array.from({ length: endP - startP + 1 }, (_, i) => startP + i);

  return (
    <div className="adm-pg-footer">
      <span className="adm-pg-info">
        {t('admin.pager.showing', { range: total ? `${start + 1}–${end}` : '0', total })}
      </span>
      <div className="adm-pg-controls">
        {pages > 1 && (
          <>
            <button type="button" className="adm-pg-btn" disabled={safePage <= 1} onClick={() => onPage(safePage - 1)} aria-label={t('admin.pager.prev')}>‹</button>
            {numbers.map((p) => (
              <button key={p} type="button" aria-current={p === safePage ? 'page' : undefined}
                className={`adm-pg-btn ${p === safePage ? 'adm-pg-btn--on' : ''}`} onClick={() => onPage(p)}>{p}</button>
            ))}
            <button type="button" className="adm-pg-btn" disabled={safePage >= pages} onClick={() => onPage(safePage + 1)} aria-label={t('admin.pager.next')}>›</button>
          </>
        )}
        <select className="adm-pg-size" value={pageSize} aria-label={t('admin.pager.pageSize')}
          onChange={(e) => onPageSize(Number(e.target.value))}>
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>{s === 0 ? t('admin.pager.all') : t('admin.pager.perPage', { size: s })}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  description?: string;
  /**
   * Bug 03 — slot opcional dentro del header (`adm-section-head`), debajo del título/descripción.
   * Primer consumidor: el banner amarillo de "no editable con campaña activa" de `AdminPoints`.
   * Es un `ReactNode` genérico y no un booleano "warning" con texto propio a propósito: distintas
   * secciones podrían necesitar avisos de otro tipo (o ninguno) sin tener que ampliar esta prop cada
   * vez.
   */
  headerNote?: ReactNode;
  children: ReactNode;
}

/** Encabezado + contenedor estándar de una sección de admin. */
export function AdminSection({ title, description, headerNote, children }: Readonly<SectionProps>) {
  return (
    <section className="adm-section">
      <header className="adm-section-head">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
        {headerNote}
      </header>
      {children}
    </section>
  );
}

interface LocalizedFieldsProps {
  label: string;
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  /** Usa textarea en lugar de input (descripciones largas). */
  textarea?: boolean;
  /** Idiomas obligatorios: se marca con * si falta (i18n ×7). */
  requireAll?: boolean;
  disabled?: boolean;
  /** Muestra el botón "Traducir a los demás idiomas" (Azure Translator, mismo servicio que ya usa
   *  AdminCampaigns). Toma el primer idioma con texto como origen. */
  translatable?: boolean;
}

/**
 * Editor de un texto multiidioma (i18n ×7: es/en/de/pt/nl/fr/ro). Reutilizado por catálogo,
 * gestión (racha/insignias), campañas y pills. Renderiza un campo por idioma soportado con su
 * bandera; marca en rojo los idiomas faltantes cuando `requireAll`. Nada hardcodeado.
 */
export function LocalizedFields({ label, value, onChange, textarea, requireAll, disabled, translatable }: Readonly<LocalizedFieldsProps>) {
  const { t } = useTranslation('recognition');
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const set = (code: string, text: string) => onChange({ ...value, [code]: text });

  const sourceLang = SUPPORTED_LANGUAGES.find((lng) => (value[lng.code] ?? '').trim())?.code ?? null;

  const handleTranslate = async () => {
    if (!sourceLang || translating) return;
    setTranslateError(null);
    setTranslating(true);
    try {
      const targetLangs = SUPPORTED_LANGUAGES.map((lng) => lng.code).filter((code) => code !== sourceLang);
      const response = await translationApi.translateTexts({
        sourceLang, targetLangs, texts: [value[sourceLang] ?? ''],
      });
      const next = { ...value };
      targetLangs.forEach((code) => {
        next[code] = response.translations[code]?.[0] ?? next[code] ?? '';
      });
      onChange(next);
    } catch (error) {
      setTranslateError(
        error instanceof SessionExpiredError ? t('admin.translate.sessionExpired') : t('admin.translate.error'),
      );
      if (import.meta.env.DEV) console.error(error);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="adm-field">
      <span>{label}</span>
      {translatable && (
        <div className="adm-translate-row">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={!sourceLang || translating || disabled}
            onClick={handleTranslate}
            title={sourceLang ? t('admin.translate.buttonFrom', { lang: sourceLang.toUpperCase() }) : t('admin.translate.hint')}
          >
            <i className={`fas ${translating ? 'fa-spinner fa-spin' : 'fa-language'}`} />
            {' '}
            {translating ? t('admin.translate.loading') : t('admin.translate.button')}
          </button>
          {translateError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {translateError}</p>}
        </div>
      )}
      <div className="adm-i18n-grid">
        {SUPPORTED_LANGUAGES.map((lng) => {
          const missing = requireAll && !(value[lng.code] ?? '').trim();
          return (
            <label key={lng.code} className={`adm-i18n-cell ${missing ? 'adm-i18n-cell--missing' : ''}`}>
              <span className="adm-i18n-lang">{lng.flag} {lng.displayCode}{requireAll && ' *'}</span>
              {textarea ? (
                <textarea className="adm-i18n-textarea" value={value[lng.code] ?? ''} disabled={disabled}
                  onChange={(e) => set(lng.code, e.target.value)} />
              ) : (
                <input type="text" value={value[lng.code] ?? ''} disabled={disabled}
                  onChange={(e) => set(lng.code, e.target.value)} />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** true si el texto multiidioma tiene los 7 idiomas soportados con contenido no vacío. */
export function hasAllLanguages(value: LocalizedText): boolean {
  return SUPPORTED_LANGUAGES.every((l) => (value[l.code] ?? '').trim().length > 0);
}

// ─── Formulario compartido "Banner de Brillamos Juntos" (Tarea 14 SDD 2026-08-17) ─────────────
// Extraído de `BrillamosBannerEditor` (AdminCampaigns.tsx) — el editor GLOBAL del banner (solo Admin
// Global). El wizard de excepciones (CampaignExceptions.tsx) necesita el MISMO formulario para
// proponer un override POR LOCALIDAD, así que "subtítulo por idioma + logo por idioma + preview" vive
// aquí una sola vez y ambos lo consumen. Controlado 100% por `value`/`onChange`: la fuente de datos
// (GestionSettingsDto global vs. CampaignExceptionOverrideDto de una excepción) y el guardado quedan
// en cada caller — este componente no sabe nada de persistencia.
export interface BannerEditorValue {
  subtitle: Record<string, string>;
  logoUrl: Record<string, string>;
}

interface BannerEditorFieldsProps {
  value: BannerEditorValue;
  onChange: (next: BannerEditorValue) => void;
  /** Logo mostrado en la preview cuando el idioma activo no tiene uno propio subido. Default: el
   *  Shine blanco (mismo asset que /app/brillamos). */
  defaultLogo?: string;
  /** Etiqueta opcional para distinguir visualmente de QUIÉN es este banner — p. ej. "esto sobrescribe
   *  el banner global solo para el alcance de esta excepción". Sin ella, el formulario no aclara
   *  contexto (uso: `BrillamosBannerEditor`, el banner global no necesita esta aclaración). */
  scopeLabel?: string;
}

const BANNER_EDITOR_DEFAULT_LOGO = asset('/brillamos/logo-shine-white.svg');
const BANNER_EDITOR_MAX_LOGO_BYTES = 500 * 1024; // 500 KB: el logo va embebido en el doc; se acota el tamaño.

export function BannerEditorFields({ value, onChange, defaultLogo = BANNER_EDITOR_DEFAULT_LOGO, scopeLabel }: Readonly<BannerEditorFieldsProps>) {
  const { t } = useTranslation('recognition');
  const [bannerLang, setBannerLang] = useState('es');
  const subtitle = value.subtitle[bannerLang] ?? '';
  const patchSubtitle = (text: string) => onChange({ ...value, subtitle: { ...value.subtitle, [bannerLang]: text } });

  // Traduce el subtítulo desde `bannerLang` (idioma con el chip activo) hacia los otros 6, reusando el
  // mismo Azure Translator que el resto del admin.
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);
  const handleTranslate = async () => {
    if (translating) return;
    setTranslateError(null);
    const targetLangs = SUPPORTED_LANGUAGES.map((lng) => lng.code).filter((code) => code !== bannerLang);
    setTranslating(true);
    try {
      const response = await translationApi.translateTexts({ sourceLang: bannerLang, targetLangs, texts: [subtitle] });
      const filled = { ...value.subtitle };
      targetLangs.forEach((code) => { filled[code] = response.translations[code]?.[0] ?? filled[code] ?? ''; });
      onChange({ ...value, subtitle: filled });
    } catch (error) {
      setTranslateError(
        error instanceof SessionExpiredError ? t('admin.translate.sessionExpired') : t('admin.translate.error'),
      );
      if (import.meta.env.DEV) console.error(error);
    } finally {
      setTranslating(false);
    }
  };

  // Logo: "Subir PNG" lo sobre-escribe como data-URI en value.logoUrl[bannerLang]; "Volver al
  // predeterminado" quita la clave (cae a `defaultLogo`).
  const uploadedLogo = value.logoUrl[bannerLang];
  const currentLogo = uploadedLogo || defaultLogo;
  const setLogo = (next: string) => {
    const nextLogoUrl = { ...value.logoUrl };
    if (next) nextLogoUrl[bannerLang] = next; else delete nextLogoUrl[bannerLang];
    onChange({ ...value, logoUrl: nextLogoUrl });
  };
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const handleLogoFile = (file: File | null) => {
    setLogoError(null);
    if (!file) return;
    if (file.type !== 'image/png') { setLogoError(t('admin.campaigns.bannerLogoErrPng')); return; }
    if (file.size > BANNER_EDITOR_MAX_LOGO_BYTES) { setLogoError(t('admin.campaigns.bannerLogoErrSize')); return; }
    const reader = new FileReader();
    reader.onload = () => setLogo(String(reader.result));
    reader.onerror = () => setLogoError(t('admin.campaigns.bannerLogoErrPng'));
    reader.readAsDataURL(file);
  };

  return (
    <>
      {scopeLabel && (
        <p className="adm-note adm-banner-scope-label"><i className="fas fa-map-marker-alt" aria-hidden="true" /> {scopeLabel}</p>
      )}
      <div className="adm-banner-langs">
        {SUPPORTED_LANGUAGES.map((lng) => (
          <button key={lng.code} type="button"
            className={`adm-banner-lang ${bannerLang === lng.code ? 'adm-banner-lang--on' : ''}`}
            onClick={() => setBannerLang(lng.code)}><span aria-hidden="true">{lng.flag}</span> {lng.code.toUpperCase()}</button>
        ))}
      </div>
      <div className="adm-banner-grid">
        <div className="adm-banner-fields">
          <label className="adm-field"><span>{t('admin.campaigns.bannerSubtitle')}</span>
            <textarea rows={2} value={subtitle} onChange={(e) => patchSubtitle(e.target.value)} /></label>
          <div className="admin-docs-actions">
            <button
              type="button"
              className="btn btn--secondary"
              disabled={translating || !subtitle}
              onClick={handleTranslate}
            >
              <i className={`fas ${translating ? 'fa-spinner fa-spin' : 'fa-language'}`} />
              {' '}
              {translating ? t('admin.translate.loading') : t('admin.translate.button')}
            </button>
          </div>
          {translateError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {translateError}</p>}
          <div className="adm-field">
            <span>{t('admin.campaigns.bannerLogo')}</span>
            <div className="adm-banner-logo-row">
              <img src={currentLogo} alt="" className="adm-banner-logo-thumb" />
              <input ref={logoFileRef} type="file" accept="image/png" className="adm-hidden-input" onChange={(e) => { handleLogoFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
              <button type="button" className="btn btn--secondary" onClick={() => logoFileRef.current?.click()}>
                <i className="fas fa-upload" /> {t('admin.campaigns.bannerLogoUpload')}
              </button>
              {uploadedLogo && <button type="button" className="btn btn--link" onClick={() => setLogo('')}>{t('admin.campaigns.bannerLogoReset')}</button>}
            </div>
            {logoError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {logoError}</p>}
            <small className="adm-note">{t('admin.campaigns.brillamosBannerLogoHint')}</small>
          </div>
        </div>
        <div className="adm-banner-preview">
          <span className="adm-banner-preview-label">{t('admin.campaigns.bannerPreview')}</span>
          <div className="adm-banner-card adm-banner-card--brillamos">
            <img src={currentLogo} alt="" className="adm-banner-card-logo" />
            {subtitle && <p className="adm-banner-card-sub">{subtitle}</p>}
            <span className="adm-banner-card-btn"><i className="fas fa-star" /> {t('admin.campaigns.bannerCta')}</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Gestor de documentos compartido (Tarea 15 SDD 2026-08-17) ─────────────────────────────────
// Extraído de `CampaignDocumentsStep` (AdminCampaigns.tsx, WP-08 §26) — formulario de alta (idioma +
// categoría + PDF + título multi-idioma) más la tabla de ya subidos. El wizard de excepciones
// ("Campañas por OU") necesita el MISMO formulario, apuntado a los endpoints de excepción en vez de
// campaña (Tarea 15), así que vive aquí una sola vez y ambos lo consumen — mismo criterio que
// `BannerEditorFields` arriba. 100% controlado por props: no sabe si detrás hay una campaña o una
// excepción, ni qué hook de mutación lo respalda.
//
// WP-08 (§26) — categorías de documento. Vocabulario CANÓNICO del visor de la landing
// (`CampaignDocs.tsx`: CATEGORY_ICON + i18n docs.cat.*), para que el doc subido renderice con su
// ícono y su etiqueta traducida en vez del key crudo.
export const DOC_CATEGORIES = ['howto', 'awards', 'legal', 'privacy', 'cookies', 'notice'] as const;

const DOC_MAX_PDF_BYTES = 25 * 1024 * 1024;

export interface DocumentsManagerProps {
  docs: CampaignDocumentDto[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onAdd: (input: UploadCampaignDocumentInput) => void;
  addPending: boolean;
  addError: unknown;
  /** Transición false→true dispara la limpieza del formulario (archivo/título/categoría) — mismo
   *  momento en que antes se limpiaba dentro del `onSuccess` de la mutación. */
  addSuccess: boolean;
  onDelete: (documentId: string) => void;
  deletePending: boolean;
  /** Limpia el estado (éxito/error) de la mutación de alta al elegir un archivo nuevo — mismo
   *  patrón que `addM.reset()` en el `CampaignDocumentsStep` original. */
  onAddReset?: () => void;
}

/** Formulario de alta de documentos + tabla de los ya subidos. Ver cabecera de sección arriba. */
export function DocumentsManager({
  docs, isLoading, isError, error, onRetry, onAdd, addPending, addError, addSuccess, onDelete,
  deletePending, onAddReset,
}: Readonly<DocumentsManagerProps>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];

  const [titulo, setTitulo] = useState<LocalizedText>({});
  const [languageCode, setLanguageCode] = useState('es');
  const [categoria, setCategoria] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Limpia el formulario en la transición false→true de `addSuccess` (alta recién confirmada por el
  // servidor) — mismo momento en que antes se limpiaba dentro del `onSuccess` de la mutación.
  useEffect(() => {
    if (!addSuccess) return;
    setFile(null);
    setTitulo({});
    setCategoria('');
    if (fileRef.current) fileRef.current.value = '';
  }, [addSuccess]);

  const pickFile = (picked: File | null) => {
    setFileError(null);
    onAddReset?.();
    if (!picked) { setFile(null); return; }
    const isPdf = picked.type === 'application/pdf' || picked.name.toLowerCase().endsWith('.pdf');
    if (!isPdf || picked.size <= 0 || picked.size > DOC_MAX_PDF_BYTES) {
      setFileError(t('admin.docs.corp.soloPdf'));
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFile(picked);
  };

  // BUG (Julio, 2026-08-21) — antes solo exigía el archivo: sin categoría ni título, el alta se
  // aceptaba igual, y el documento quedaba mostrándose sin título en la tabla de abajo (y en el visor
  // público, CampaignDocs.tsx) — `d.titulo?.[lang] ?? d.fileName ?? d.url` caía al nombre del archivo
  // o a la URL cruda para cualquier idioma sin título. Mismo criterio que ya usa el resto del panel
  // para un texto multiidioma obligatorio (`hasAllLanguages` + `requireAll`, ver AdminGestion.tsx):
  // se exige completo en los 7 idiomas, no solo en uno, porque el título se busca por el idioma de
  // CADA visitante — falta uno solo y ese visitante ve el mismo problema.
  const categoriaOk = categoria.trim().length > 0;
  const tituloOk = hasAllLanguages(titulo);
  const add = () => {
    if (!file || !categoriaOk || !tituloOk) return;
    onAdd({ file, titulo, languageCode, categoria: categoria || undefined });
  };

  return (
    <>
      <div className="adm-form-grid">
        {/* 🔴 WP-B1 — ranura por idioma con bandera (no texto libre): el consumidor casa por
            igualdad exacta (`CampaignDocs`), así que un código inventado subiría un PDF que no se ve
            en ningún idioma. «Todos los idiomas» (`*`) es RN-C04: un solo archivo sirve a todos. */}
        <label className="adm-field"><span>{t('admin.campaigns.docLang')}</span>
          <select className="field" value={languageCode} onChange={(e) => setLanguageCode(e.target.value)}>
            {SUPPORTED_LANGUAGE_CODES.map((code) => (
              <option key={code} value={code}>
                {SUPPORTED_LANGUAGES.find((lng) => lng.code === code)?.flag ?? ''} {code.toUpperCase()}
              </option>
            ))}
            <option value={COMODIN_IDIOMA}>🌐 {t('docs.allLanguages')}</option>
          </select>
        </label>
        <label className="adm-field"><span>{t('admin.campaigns.docCategoria')}</span>
          <select className="field" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">{t('admin.campaigns.docCatNone')}</option>
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{t(`docs.cat.${c}`)}</option>)}
          </select>
        </label>
        <div className="adm-field"><span>{t('admin.docs.corp.archivo')}</span>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="adm-camp-file-input--hidden"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <button type="button" className="btn btn--secondary" onClick={() => fileRef.current?.click()}>
            <i className="fas fa-file-arrow-up" /> {file ? file.name : t('admin.docs.corp.elegirArchivo')}
          </button>
        </div>
      </div>
      <LocalizedFields label={t('admin.campaigns.docTitle')} value={titulo} onChange={setTitulo} requireAll translatable />
      {!categoriaOk && <p className="adm-note adm-note--warn"><i className="fas fa-triangle-exclamation" /> {t('admin.campaigns.docCategoryRequired')}</p>}
      {!tituloOk && <p className="adm-note adm-note--warn"><i className="fas fa-language" /> {t('admin.campaigns.docTitleRequired')}</p>}
      {fileError && <p className="adm-error" role="alert"><i className="fas fa-triangle-exclamation" /> {fileError}</p>}
      {addError != null && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(addError, t('admin.saveError'))}
        </p>
      )}
      <button type="button" className="btn btn--primary" disabled={!file || !categoriaOk || !tituloOk || addPending} onClick={add}>
        <i className="fas fa-plus" /> {t('admin.campaigns.addDoc')}
      </button>

      <div className="adm-camp-docs-block">
        <StateBlock isLoading={isLoading} isError={isError} error={error} isEmpty={docs.length === 0} emptyText={t('admin.campaigns.docsEmpty')} onRetry={onRetry}>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr><th>{t('admin.campaigns.docTitle')}</th><th>{t('admin.campaigns.docLang')}</th><th /></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td>{d.titulo?.[lang] ?? d.fileName ?? d.url}</td>
                    <td>{d.languageCode === COMODIN_IDIOMA ? t('docs.allLanguages') : d.languageCode.toUpperCase()}</td>
                    <td><button type="button" className="btn btn--danger" disabled={deletePending} onClick={() => onDelete(d.id)}><i className="fas fa-trash-can" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </StateBlock>
      </div>
    </>
  );
}

/**
 * Los años que ofrece el filtro: el actual, el siguiente y los tres anteriores.
 *
 * Se calculan y no van escritos a mano como en la maqueta —que lista 2024-2027— porque una lista fija
 * caduca: en 2028 el filtro no ofrecería el año en curso y el histórico editable (3 años) quedaría
 * fuera de alcance sin que nadie se diera cuenta.
 *
 * Vive aquí y no en `AdminWinners` desde la fila 40: el editor de Insignias de Campaña ofrece el
 * MISMO rango para el año de la medalla, y tiene que ser el mismo o el desplegable de la insignia
 * podría proponer un año que el del ganador no admite — que es exactamente el desajuste que la
 * validación de otorgamiento existe para impedir.
 */
export function aniosDisponibles(hoy: Date = new Date()): number[] {
  const actual = hoy.getFullYear();

  // 🔴 Sin `actual + 1`: `Winner.ValidateAnio` rechaza cualquier año futuro, así que era una opción
  // del desplegable que NUNCA podía devolver una fila. La maqueta lista 2027 porque tiene los años
  // escritos a mano; aquí se calculan, y calcular una opción imposible es peor que no ofrecerla.
  return [actual, actual - 1, actual - 2, actual - 3];
}
